// 계약 후 진행 타임라인 + 달성 페이스 차트 — ops-britzmedi margin/ContractTimeline.tsx 이식.
// (변경점: datalabels 옵션 제거, chart.js 등록은 MarginCharts.tsx 에서 수행)
//  · Timeline: 계약일→쿼터 종료의 시간축 위에 데모 선반입(파랑)·인허가(앰버)·MOQ 쿼터(초록) 단계 + 오늘 마커
//  · PaceChart: 달성 전제 목표 누적(파랑 점선) vs 실적 누적(초록 실선) vs 현재 페이스 전망(앰버 점선)
// 축 계산 = @/lib/timeline/scale 단일 출처 (Design §2.3) — 로컬자정 파싱·epoch-ms 대신 정수 UTC day-index.
// 불변: 축 범위에 오늘(now)을 **넣지 않는다** — 넣으면 날짜를 안 바꿔도 내일이면 바 위치가 달라졌다 (§2.3, 이번에 제거).
// 잔존(v2): 하드코딩 slate/blue 색(C)은 MANIFEST 팔레트가 아니다(§8.1). 색 교체는 별도 과제 — 이번 변경은 축 계산만 건드린다.
// PaceChart 는 손대지 않는다 — d2t/fmtD/DAY 는 그쪽 전용으로 존치.
import { Line } from "react-chartjs-2";
import "./MarginCharts"; // chart.js component registration side-effect
import type { Contract, SaleRow } from "@/lib/margin/model";
import { usd } from "@/lib/margin/model";
import { barPct, dayToPct, fmtShort, fromDayIndex, makeScale, toDayIndex } from "@/lib/timeline/scale";

const C = { demo: "#2563eb", license: "#d97706", quota: "#059669", ink: "#334155", muted: "#94a3b8", grid: "#e2e8f0" };
const DAY = 86_400_000;
const d2t = (s: string) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(s + "T00:00:00").getTime() : null);
const fmtD = (t: number) => {
  const d = new Date(t);
  return `${String(d.getFullYear()).slice(2)}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * "YYYY-MM-DD" → 정수 UTC day-index. 빈 문자열·존재하지 않는 날짜(2026-02-31)는 null.
 * toDayIndex 는 롤오버를 throw 로 막으므로, 렌더 경로에서는 반드시 감싼다 — 여기서 throw = 화이트스크린.
 */
function dayOrNull(s: string | null | undefined): number | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  try {
    return toDayIndex(s);
  } catch {
    return null;
  }
}

/** 시간축 단계 타임라인. */
export function ContractTimeline({ ct, today }: { ct: Contract; today: string }): JSX.Element {
  // 축 범위 = 계약 날짜만. 오늘(now)은 제외 — 축은 날짜를 고쳐야만 움직인다 (§2.3).
  const pts = [
    dayOrNull(ct.startDate),
    dayOrNull(ct.demoStart),
    dayOrNull(ct.demoEnd),
    dayOrNull(ct.licenseStart),
    dayOrNull(ct.licenseEnd),
    dayOrNull(ct.quotaStart),
    dayOrNull(ct.endDate),
  ].filter((x): x is number => x != null);
  if (pts.length < 2) {
    return <p className="text-[11px] text-slate-400">계약일·쿼터 종료일 등 날짜를 입력하면 단계 타임라인이 그려집니다.</p>;
  }
  const minDay = Math.min(...pts);
  const maxDay = Math.max(...pts);
  const scale = makeScale(fromDayIndex(minDay), fromDayIndex(maxDay));
  const nowDay = dayOrNull(today);

  const seg = (from: string, to: string, color: string, label: string, row: number) => {
    const f = dayOrNull(from);
    const t = dayOrNull(to);
    if (f == null || t == null || t < f) return null;
    // 반열린 [from, to+1) — 하루짜리 단계도 폭 > 0 (결정4).
    const g = barPct(from, to, scale);
    return (
      <div
        key={label}
        className="absolute flex items-center overflow-hidden rounded px-1 text-[10px] font-semibold text-white"
        style={{
          left: `${g.left}%`,
          // 2% 하한 = 기존 가독 floor 승계. 여기는 읽기 전용이라 px→날짜 역변환이 없어 §7.4 금지에 걸리지 않는다.
          width: `${Math.max(g.width, 2)}%`,
          top: `${row * 18}px`,
          height: "16px",
          background: color,
        }}
        title={`${label}: ${from} ~ ${to}`}
      >
        <span className="truncate">{label}</span>
      </div>
    );
  };

  const quotaFrom = ct.quotaStart || ct.startDate;
  const quotaDay = dayOrNull(quotaFrom);
  const nowVisible = nowDay != null && nowDay >= minDay && nowDay <= maxDay;
  const nowPct = nowDay != null ? dayToPct(nowDay, scale) : 0;
  return (
    <div className="mt-2">
      <div className="relative h-[58px] w-full rounded border border-slate-200 bg-white">
        {seg(ct.demoStart, ct.demoEnd, C.demo, `데모 선반입${ct.demoQty ? ` ${ct.demoQty}대` : ""}`, 0)}
        {seg(ct.licenseStart, ct.licenseEnd, C.license, `${ct.licenseLabel || "인허가"}${ct.licenseDone ? " ✓완료" : " 진행"}`, 1)}
        {seg(quotaFrom, ct.endDate, C.quota, "MOQ 쿼터 기간", 2)}
        {/* 오늘 마커 — 축 범위 밖이면 렌더하지 않는다(축이 now 를 담지 않으므로 밖에 있을 수 있다). */}
        {nowVisible && (
          <>
            <div className="absolute inset-y-0 w-0.5 bg-red-500" style={{ left: `${nowPct}%` }} />
            <span
              className="absolute -top-0 rounded bg-red-500 px-1 text-[9px] font-bold text-white"
              style={{ left: `min(max(${nowPct}% - 12px, 0px), calc(100% - 30px))` }}
            >
              오늘
            </span>
          </>
        )}
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] text-slate-400 tabular-nums">
        <span>{fmtShort(fromDayIndex(minDay))}</span>
        {quotaDay != null && <span className="text-emerald-700">쿼터 기산 {fmtShort(fromDayIndex(quotaDay))}</span>}
        <span>{fmtShort(fromDayIndex(maxDay))}</span>
      </div>
    </div>
  );
}

/** 달성 전제 목표 vs 실적 vs 전망 누적 곡선. */
export function PaceChart({
  ct,
  sales,
  target,
  today,
}: {
  ct: Contract;
  sales: SaleRow[];
  target: number;
  today: string;
}): JSX.Element | null {
  const qs = d2t(ct.quotaStart || ct.startDate);
  const qe = d2t(ct.endDate);
  const now = d2t(today);
  if (qs == null || qe == null || now == null || qe <= qs) return null;

  // 월 단위 눈금 생성
  const ticks: number[] = [];
  for (let t = qs; t <= qe; t += 30.44 * DAY) ticks.push(Math.round(t));
  if (ticks[ticks.length - 1]! < qe) ticks.push(qe);

  const rows = sales
    .filter((s) => s.contractId === ct.id && d2t(s.date) != null)
    .sort((a, b) => a.date.localeCompare(b.date));
  const cumAt = (t: number) => rows.filter((r) => d2t(r.date)! <= t).reduce((s, r) => s + (r.qty || 0), 0);
  const soldNow = cumAt(now);
  const elapsed = Math.min(Math.max(now - qs, 0), qe - qs);
  const paceEnd = elapsed > 0 ? (soldNow / elapsed) * (qe - qs) : null;

  return (
    <div className="h-52">
      <Line
        data={{
          labels: ticks.map(fmtD),
          datasets: [
            {
              label: "달성 전제 목표 누적",
              data: ticks.map((t) => (target * (t - qs)) / (qe - qs)),
              borderColor: C.demo,
              backgroundColor: C.demo,
              borderDash: [6, 4],
              borderWidth: 2,
              pointRadius: 0,
              pointHitRadius: 10,
            },
            {
              label: "실적 누적",
              data: ticks.map((t) => (t <= now ? cumAt(t) : NaN)),
              borderColor: C.quota,
              backgroundColor: C.quota,
              borderWidth: 2.5,
              pointRadius: 0,
              pointHitRadius: 10,
              stepped: "before" as const,
            },
            {
              label: "현재 페이스 전망",
              data: ticks.map((t) => (paceEnd != null && t >= now ? (soldNow / elapsed) * (t - qs) : NaN)),
              borderColor: C.license,
              backgroundColor: C.license,
              borderDash: [2, 3],
              borderWidth: 2,
              pointRadius: 0,
              pointHitRadius: 10,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          scales: {
            x: { grid: { display: false }, ticks: { color: C.muted, font: { size: 9 }, maxTicksLimit: 8 } },
            y: {
              grid: { color: C.grid },
              border: { display: false },
              ticks: { color: C.muted, font: { size: 9 }, callback: (v) => usd(Number(v)) + "대" },
              suggestedMax: target * 1.1,
            },
          },
          plugins: {
            legend: { position: "bottom", labels: { color: C.ink, boxWidth: 10, boxHeight: 10, font: { size: 10 } } },
            tooltip: {
              callbacks: { label: (x) => ` ${x.dataset.label}: ${usd(Math.round((x.parsed.y ?? NaN) * 10) / 10)}대` },
            },
          },
        }}
      />
    </div>
  );
}
